import { useState, useRef, useEffect, useMemo, Fragment } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  format, startOfDay, subDays, startOfMonth, endOfMonth,
  subMonths, startOfYear, isSameDay, isBefore, isAfter, differenceInDays
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';

const PRESETS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: '7days', label: '7 jours' },
  { id: 'month', label: 'Ce mois' },
  { id: 'year', label: 'Cette année' },
];

const SHORTCUTS = [
  { id: 'today', label: "Aujourd'hui" },
  { id: 'yesterday', label: 'Hier' },
  { id: '7days', label: '7 derniers jours' },
  { id: '30days', label: '30 derniers jours' },
  { id: 'month', label: 'Ce mois' },
  { id: 'lastMonth', label: 'Mois dernier' },
  { id: 'year', label: 'Cette année' },
];

const PRESET_IDS = PRESETS.map(p => p.id);
const DAY_LABELS = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

function getPresetRange(id) {
  const now = new Date();
  const today = startOfDay(now);
  switch (id) {
    case 'today': return { start: today, end: today };
    case 'yesterday': { const y = subDays(today, 1); return { start: y, end: y }; }
    case '7days': return { start: subDays(today, 6), end: today };
    case '30days': return { start: subDays(today, 29), end: today };
    case 'month': return { start: startOfMonth(now), end: today };
    case 'lastMonth': { const lm = subMonths(now, 1); return { start: startOfMonth(lm), end: endOfMonth(lm) }; }
    case 'year': return { start: startOfYear(now), end: today };
    default: return { start: today, end: today };
  }
}

function getBadgeText(presetId, range) {
  const labels = {
    today: "Aujourd'hui", yesterday: 'Hier',
    '7days': '7 derniers jours', '30days': '30 derniers jours',
    month: 'Ce mois', lastMonth: 'Mois dernier', year: 'Cette année',
  };
  if (presetId && labels[presetId]) return labels[presetId];
  if (range?.start && range?.end) {
    if (isSameDay(range.start, range.end)) return format(range.start, 'd MMM', { locale: fr });
    return `${format(range.start, 'd MMM', { locale: fr })} → ${format(range.end, 'd MMM', { locale: fr })}`;
  }
  return 'Personnalisé';
}

function getMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let startDow = firstDay.getDay();
  startDow = startDow === 0 ? 6 : startDow - 1;

  const days = [];
  for (let i = startDow - 1; i >= 0; i--) {
    days.push({ date: new Date(year, month, -i), currentMonth: false });
  }
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push({ date: new Date(year, month, d), currentMonth: true });
  }
  const totalCells = Math.ceil(days.length / 7) * 7;
  for (let d = 1; d <= totalCells - days.length; d++) {
    days.push({ date: new Date(year, month + 1, d), currentMonth: false });
  }
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

/* ── CalendarMonth ── */
function CalendarMonth({ year, month, isDark, range, onDayClick, onDayHover, onDayLeave, onNavLeft, onNavRight }) {
  const [hovered, setHovered] = useState(null);
  const weeks = useMemo(() => getMonthGrid(year, month), [year, month]);
  const label = format(new Date(year, month), 'MMMM yyyy', { locale: fr });
  const todayDate = useMemo(() => startOfDay(new Date()), []);

  const navBtnStyle = {
    width: 26, height: 26, borderRadius: 6,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB'}`,
    background: 'transparent',
    color: isDark ? 'rgba(255,255,255,0.5)' : '#6B7280',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        {onNavLeft
          ? <button onClick={onNavLeft} style={navBtnStyle} data-testid="cal-nav-left"><ChevronLeft style={{ width: 14, height: 14 }} /></button>
          : <div style={{ width: 26 }} />}
        <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.8)' : '#111827', textTransform: 'capitalize' }}>
          {label}
        </span>
        {onNavRight
          ? <button onClick={onNavRight} style={navBtnStyle} data-testid="cal-nav-right"><ChevronRight style={{ width: 14, height: 14 }} /></button>
          : <div style={{ width: 26 }} />}
      </div>

      {/* Day-of-week row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: 4 }}>
        {DAY_LABELS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.06em', color: isDark ? 'rgba(255,255,255,0.25)' : '#9CA3AF', padding: '4px 0' }}>{d}</div>
        ))}
      </div>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {week.map((day, di) => {
            const d = day.date;
            const cm = day.currentMonth;
            const isToday = isSameDay(d, todayDate) && cm;

            const isStart = range?.start && isSameDay(d, range.start);
            const isEnd = range?.end && isSameDay(d, range.end);
            const single = isStart && isEnd;
            const inRange = range?.start && range?.end && !single &&
              !isSameDay(d, range.start) && !isSameDay(d, range.end) &&
              isAfter(d, range.start) && isBefore(d, range.end);

            const isHov = hovered && cm && isSameDay(d, hovered) && !isStart && !isEnd && !inRange && !single;

            let bg = 'transparent';
            let color = cm
              ? (isDark ? 'rgba(255,255,255,0.55)' : '#374151')
              : (isDark ? 'rgba(255,255,255,0.15)' : '#D1D5DB');
            let fw = 400;
            let br = '6px';

            if (isToday) { color = '#FF4500'; fw = 700; }
            if (single) { bg = '#FF4500'; color = '#fff'; br = '6px'; fw = 600; }
            else if (isStart) { bg = '#FF4500'; color = '#fff'; br = '6px 0 0 6px'; fw = 600; }
            else if (isEnd) { bg = '#FF4500'; color = '#fff'; br = '0 6px 6px 0'; fw = 600; }
            else if (inRange) { bg = isDark ? 'rgba(255,69,0,0.12)' : 'rgba(255,69,0,0.08)'; color = 'rgba(255,120,60,0.9)'; br = '0'; }
            if (isHov) { bg = isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6'; if (!isToday) color = isDark ? '#fff' : '#111827'; }

            return (
              <div
                key={di}
                data-testid={cm ? `day-${d.getDate()}-${month}` : undefined}
                onClick={() => cm && onDayClick(d)}
                onMouseEnter={() => { if (cm) { setHovered(d); onDayHover(d); } }}
                onMouseLeave={() => { setHovered(null); onDayLeave(); }}
                style={{
                  textAlign: 'center', fontSize: 12, padding: '6px 2px',
                  borderRadius: br, color, fontWeight: fw, background: bg,
                  cursor: cm ? 'pointer' : 'default',
                  transition: 'background 0.1s,color 0.1s', userSelect: 'none',
                }}
              >
                {d.getDate()}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ── PremiumDatePicker ── */
export function PremiumDatePicker({ isDark, activePreset, appliedRange, onApply }) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState({ start: null, end: null });
  const [draftShortcut, setDraftShortcut] = useState(null);
  const [hover, setHover] = useState(null);
  const [calMonth, setCalMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        handleCancel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  /* ── Handlers ── */
  const handlePresetClick = (id) => {
    const range = getPresetRange(id);
    onApply(id, range);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!isOpen) {
      setDraft(appliedRange || getPresetRange('today'));
      setDraftShortcut(activePreset);
      const s = appliedRange?.start || new Date();
      setCalMonth({ year: s.getFullYear(), month: s.getMonth() });
    }
    setIsOpen(v => !v);
  };

  const handleShortcutClick = (id) => {
    const range = getPresetRange(id);
    setDraft(range);
    setDraftShortcut(id);
    setHover(null);
    setCalMonth({ year: range.start.getFullYear(), month: range.start.getMonth() });
  };

  const handleDayClick = (d) => {
    const day = startOfDay(d);
    if (!draft.start || draft.end) {
      setDraft({ start: day, end: null });
      setDraftShortcut(null);
    } else {
      let s = draft.start, e = day;
      if (isBefore(e, s)) [s, e] = [e, s];
      setDraft({ start: s, end: e });
      setDraftShortcut(null);
      setHover(null);
    }
  };

  const handleApply = () => {
    const range = draft.start && draft.end ? draft : appliedRange;
    onApply(draftShortcut || 'custom', range);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
    setDraft(appliedRange || getPresetRange('today'));
    setDraftShortcut(activePreset);
  };

  /* ── Computed ── */
  const previewRange = useMemo(() => {
    if (draft.start && !draft.end && hover) {
      let s = draft.start, e = hover;
      if (isBefore(e, s)) [s, e] = [e, s];
      return { start: s, end: e };
    }
    return draft.start && draft.end ? draft : null;
  }, [draft, hover]);

  const dayCount = useMemo(() => {
    const r = previewRange || draft;
    if (r?.start && r?.end) return differenceInDays(r.end, r.start) + 1;
    return 0;
  }, [previewRange, draft]);

  const badgeText = getBadgeText(activePreset, appliedRange);
  const isCustomActive = isOpen || !PRESET_IDS.includes(activePreset);

  const goLeft = () => setCalMonth(p => { const d = new Date(p.year, p.month - 1); return { year: d.getFullYear(), month: d.getMonth() }; });
  const goRight = () => setCalMonth(p => { const d = new Date(p.year, p.month + 1); return { year: d.getFullYear(), month: d.getMonth() }; });
  const rightMonth = useMemo(() => { const d = new Date(calMonth.year, calMonth.month + 1); return { year: d.getFullYear(), month: d.getMonth() }; }, [calMonth]);

  /* ── Styles ── */
  const tabContainerStyle = {
    display: 'flex', alignItems: 'center',
    background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: 12, padding: 5, gap: 4,
  };

  const dividerStyle = { width: 1, height: 16, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', flexShrink: 0 };

  const tabBtn = (active) => ({
    fontSize: 12, fontWeight: active ? 600 : 500,
    padding: '6px 14px', borderRadius: 8,
    color: active ? '#FFFFFF' : (isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)'),
    background: active ? '#FF4500' : 'transparent',
    border: 'none', cursor: 'pointer', transition: 'all 0.15s ease', whiteSpace: 'nowrap',
  });

  return (
    <div className="premium-datepicker-wrapper" data-testid="premium-date-picker">

      {/* ── Period tabs strip ── */}
      <div className="premium-datepicker-tabs" style={{
        display: 'flex', alignItems: 'center',
        background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: 12, padding: 5, gap: 4,
        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none', msOverflowStyle: 'none',
      }}>
        {PRESETS.map((p, i) => (
          <Fragment key={p.id}>
            {i > 0 && <div style={dividerStyle} />}
            <button data-testid={`period-${p.id}`} onClick={() => handlePresetClick(p.id)} style={tabBtn(activePreset === p.id)}>
              {p.label}
            </button>
          </Fragment>
        ))}
        <div style={dividerStyle} />
        <button
          ref={triggerRef}
          data-testid="period-custom"
          onClick={toggleDropdown}
          style={{
            fontSize: 12, fontWeight: 500, padding: '6px 14px', borderRadius: 8,
            color: isCustomActive ? 'rgba(255,100,50,0.9)' : (isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.45)'),
            background: isCustomActive ? 'rgba(255,69,0,0.10)' : 'transparent',
            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
            transition: 'all 0.15s ease', whiteSpace: 'nowrap', flexShrink: 0,
          }}
        >
          <CalendarIcon style={{ width: 13, height: 13 }} />
          Dates
        </button>
      </div>

      {/* ── Range badge ── */}
      <div data-testid="range-badge" className="premium-datepicker-badge" style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(255,69,0,0.10)', border: '1px solid rgba(255,69,0,0.22)',
        borderRadius: 20, padding: '4px 12px', whiteSpace: 'nowrap', flexShrink: 0,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#FF4500' }} />
        <span style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,100,50,0.9)' }}>{badgeText}</span>
      </div>

      {/* ── Dropdown ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            data-testid="datepicker-dropdown"
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="premium-datepicker-dropdown"
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              background: isDark ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#E5E7EB'}`,
              borderRadius: 16, boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
              padding: 16, width: 560, zIndex: 100,
              maxWidth: 'calc(100vw - 32px)',
            }}
          >
            {/* Shortcuts */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {SHORTCUTS.map(s => {
                const active = draftShortcut === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`shortcut-${s.id}`}
                    onClick={() => handleShortcutClick(s.id)}
                    style={{
                      fontSize: 11, padding: '5px 11px', borderRadius: 8,
                      border: `1px solid ${active ? 'rgba(255,69,0,0.30)' : (isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB')}`,
                      color: active ? 'rgba(255,100,50,0.9)' : (isDark ? 'rgba(255,255,255,0.45)' : '#6B7280'),
                      background: active ? 'rgba(255,69,0,0.12)' : (isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB'),
                      cursor: 'pointer', transition: 'all 0.12s ease', whiteSpace: 'nowrap',
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>

            {/* Dual calendar */}
            <div className="premium-datepicker-calendars" style={{ display: 'grid', gap: 16, marginBottom: 16 }}>
              <CalendarMonth
                year={calMonth.year} month={calMonth.month} isDark={isDark}
                range={previewRange} onDayClick={handleDayClick}
                onDayHover={(d) => { if (draft.start && !draft.end) setHover(startOfDay(d)); }}
                onDayLeave={() => setHover(null)}
                onNavLeft={goLeft} onNavRight={null}
              />
              <CalendarMonth
                year={rightMonth.year} month={rightMonth.month} isDark={isDark}
                range={previewRange} onDayClick={handleDayClick}
                onDayHover={(d) => { if (draft.start && !draft.end) setHover(startOfDay(d)); }}
                onDayLeave={() => setHover(null)}
                onNavLeft={null} onNavRight={goRight}
              />
            </div>

            {/* Footer */}
            <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6'}`, paddingTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.35)' : '#9CA3AF' }}>
                {previewRange?.start && previewRange?.end ? (
                  <>
                    Période :{' '}
                    <span style={{ color: isDark ? 'rgba(255,255,255,0.65)' : '#374151', fontWeight: 500 }}>
                      {format(previewRange.start, 'd MMM yyyy', { locale: fr })}
                    </span>
                    {' → '}
                    <span style={{ color: isDark ? 'rgba(255,255,255,0.65)' : '#374151', fontWeight: 500 }}>
                      {format(previewRange.end, 'd MMM yyyy', { locale: fr })}
                    </span>
                    {' · '}{dayCount} jour{dayCount > 1 ? 's' : ''}
                  </>
                ) : 'Sélectionnez une période'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  data-testid="datepicker-cancel"
                  onClick={handleCancel}
                  style={{
                    fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 8,
                    background: isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6',
                    color: isDark ? 'rgba(255,255,255,0.5)' : '#6B7280',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E5E7EB'}`,
                    cursor: 'pointer', transition: 'all 0.12s ease', backdropFilter: 'blur(8px)',
                  }}
                >
                  Annuler
                </button>
                <button
                  data-testid="datepicker-apply"
                  onClick={handleApply}
                  style={{
                    fontSize: 12, fontWeight: 600, padding: '7px 14px', borderRadius: 8,
                    background: '#FF4500', color: '#FFFFFF',
                    border: 'none', cursor: 'pointer', transition: 'all 0.12s ease',
                  }}
                >
                  Appliquer →
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
